import { Box } from "@mui/material";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  FILTER_VALUE_TRIGGER_FONT_SIZE_PX,
  FILTER_VALUE_TRIGGER_FONT_WEIGHT,
} from "@/shared/ui/filters/filterTriggerStyles";
import {
  voiceClusterPhaseColors,
  voiceClusterPhaseFill,
  voiceHintFade,
  voiceHintIn,
  voiceHintOut,
} from "./aiAssist/animations";
import { voicePhaseHintLabel, type VoiceDialogPhase } from "./voiceLaunch";

const HINT_MS = 280;
const HOLD_MS = 3000;

function hintMotion(shown: boolean, played: boolean): string {
  if (shown) {
    return `${voiceHintIn} ${HINT_MS}ms ease-out both, ${voiceHintOut} ${HINT_MS}ms ease-out ${HOLD_MS}ms forwards`;
  }
  if (played) {
    return `${voiceHintOut} ${HINT_MS}ms ease-out forwards`;
  }
  return "none";
}

/**
 * Status pill to the left of a voice control. It slides out of the button
 * toward the left, stays for three seconds, then slides back. A phase or
 * pause change plays that again. The fill matches the button.
 */
export default function VoicePhaseHint({
  phase,
  paused = false,
  shown,
  inline = false,
  onOccupy,
}: Readonly<{
  phase: VoiceDialogPhase;
  paused?: boolean;
  shown: boolean;
  /** Sit in a row instead of anchoring to the left of the control. */
  inline?: boolean;
  /** Layout width of the pill, or 0 once it has slid away. Inline rows use this to clear it. */
  onOccupy?: (width: number) => void;
}>) {
  const label = voicePhaseHintLabel(phase, paused);
  const pillRef = useRef<HTMLDivElement>(null);
  const [track, setTrack] = useState({ shown: false, label: "", cycle: 0 });
  const [collapsed, setCollapsed] = useState(false);
  if (shown && (!track.shown || track.label !== label)) {
    setTrack({ shown: true, label, cycle: track.cycle + 1 });
    if (collapsed) {
      setCollapsed(false);
    }
  } else if (!shown && (track.shown || track.label !== label)) {
    setTrack({ shown: false, label, cycle: track.cycle });
  }

  useEffect(() => {
    if (!inline || !shown) {
      return undefined;
    }
    const timer = window.setTimeout(() => setCollapsed(true), HOLD_MS + HINT_MS);
    return () => window.clearTimeout(timer);
  }, [inline, shown, label]);

  useLayoutEffect(() => {
    if (!inline || onOccupy === undefined) {
      return;
    }
    onOccupy(collapsed ? 0 : (pillRef.current?.offsetWidth ?? 0));
  }, [inline, collapsed, label, shown, onOccupy, track.cycle]);

  const pill = (
    <Box
      ref={pillRef}
      key={track.cycle}
      data-voice-hint={label}
      sx={(theme) => {
        const fill =
          phase === "idle"
            ? theme.app.aiAssist.accentStrong
            : voiceClusterPhaseFill(voiceClusterPhaseColors(theme), phase);
        const wash = theme.palette.mode === "dark" ? 50 : 24;
        return {
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          height: 32,
          px: "9px",
          borderRadius: "999px",
          whiteSpace: "nowrap",
          pointerEvents: "none",
          fontFamily: theme.typography.fontFamily,
          fontSize: FILTER_VALUE_TRIGGER_FONT_SIZE_PX,
          fontWeight: FILTER_VALUE_TRIGGER_FONT_WEIGHT,
          lineHeight: 1,
          color: fill,
          backgroundColor: `color-mix(in srgb, ${fill} ${wash}%, ${theme.palette.background.default})`,
          ...(inline && collapsed ? { width: 0, minWidth: 0, px: 0, overflow: "hidden" } : {}),
          transform: shown && !collapsed ? "translateX(0)" : "translateX(100%)",
          opacity: shown && !collapsed ? 1 : 0,
          animation: hintMotion(shown, track.cycle > 0),
          "@media (prefers-reduced-motion: reduce)": {
            transform: "none",
            transition: "opacity 0.2s ease",
            animation: shown ? `${voiceHintFade} ${HOLD_MS + HINT_MS}ms linear forwards` : "none",
          },
          "&::before": {
            content: '""',
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: fill,
            flexShrink: 0,
          },
        };
      }}
    >
      {label}
    </Box>
  );

  if (inline) {
    return pill;
  }

  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        right: "calc(100% + 8px)",
        top: "50%",
        zIndex: 1,
        transform: "translateY(-50%)",
        pointerEvents: "none",
      }}
    >
      {pill}
    </Box>
  );
}
